import { Button, EmptyState, useKeyboardInset } from '@parsina/framework';
import { PatientLookupListSkeleton } from './PatientLookupSkeleton';
import { UserSearch, SearchX } from 'lucide-react';
import { C } from '@parsina/framework/clinical/src/constants/theme';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import { useAppNavigate } from '../../hooks/useAppNavigate';
import ErrorState from '@parsina/framework/clinical/src/components/common/ErrorState';
import MobileHeader from '@parsina/framework/clinical/src/components/mobile/MobileHeader';
import MobilePatientResultCard from '../../components/mobile/MobilePatientResultCard';
import usePatientSearch, { MIN_QUERY_LENGTH, RESULT_CAP } from '../../hooks/usePatientSearch';

// ─────────────────────────────────────────────────────────────────────────────
// جست‌وجوی بیمار — S12 (§B.3).
//
// The second thing a doctor does on a phone, and the app could not do it at
// all. `patients(filter, search, clinicId)` has been on the BFF the whole time
// with zero call sites, so — with no Encounters screen either — a phone opened
// exactly one thing: today's queue. A walk-in, a follow-up call about a result,
// yesterday's patient at the door: no way to the record.
//
// A CONTENT SCREEN (§C.2): the header carries the way back and the field, and
// there is no bottom nav under it — the search IS the screen, and a persistent
// bar would only compete with the results for the bottom of a keyboard-filled
// viewport.
//
// Entered from the Reception header's 🔍, beside the wallet. سوابق ویزیت‌ها
// also answers "look someone up", but through a past VISIT; this answers it
// through the person, which is the one a doctor reaches for when the name is
// all they have.
//
// The query lives in component state, never in the URL: a patient's name in an
// address is PHI in the history, in a shared link and in every log between here
// and the BFF.
// ─────────────────────────────────────────────────────────────────────────────

const PatientLookupScreen = () => {
  const { t, formatNumber } = useTranslation();
  const navigate = useAppNavigate();
  const keyboardInset = useKeyboardInset();
  const {
    query, search, clear, retry,
    results, error, truncated, state,
  } = usePatientSearch();

  const back = () => {
    // A deep link straight to /lookup has nothing to go back to; پذیرش is
    // where the search is entered from and where it belongs.
    if (window.history.length > 1) window.history.back();
    else navigate('reception');
  };

  return (
    <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: C.bg }}>

      <div style={{ flexShrink: 0 }}>
        <MobileHeader
          title={t('patient.lookup.title')}
          onBack={back}
          backTestId="lookup-back"
          search={{
            value: query,
            onChange: search,
            onClear: clear,
            placeholder: t('patient.lookup.placeholder'),
            clearLabel: t('common.actions.clearSearch'),
            // The doctor came here to type. Anything else is a wasted tap on a
            // screen whose entire purpose is one field.
            autoFocus: true,
            testId: 'lookup-input',
          }}
        />
      </div>

      <div
        data-testid="lookup-content"
        data-state={state}
        aria-busy={state === 'loading' || undefined}
        style={{
          flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch',
          // §H.1 — the results list is what the keyboard covers, so the last
          // card has to be reachable while the field is still focused.
          padding: `0.875rem 0.875rem calc(1rem + ${keyboardInset}px)`,
          display: 'flex', flexDirection: 'column', gap: '0.625rem',
        }}
      >
        {state === 'idle' && (
          <EmptyState
            icon={<UserSearch size={48} color={C.textMuted} strokeWidth={1.5} aria-hidden />}
            title={t('patient.lookup.idleTitle')}
            description={t('patient.lookup.idleBody')}
          />
        )}

        {/* One character matches most of a clinic and costs a round trip to
            say so — the gate is stated rather than silently ignoring input. */}
        {state === 'below' && (
          <div data-testid="lookup-min-chars" style={{ padding: '1rem 0', textAlign: 'center', fontSize: '0.8125rem', color: C.textMuted }}>
            {t('patient.lookup.minChars', { count: formatNumber(MIN_QUERY_LENGTH) })}
          </div>
        )}

        {state === 'loading' && <PatientLookupListSkeleton count={3} />}

        {/* A failed search is not an empty clinic. The field stays editable so
            the doctor can change the term instead of only retrying the old
            one — a typo and an outage look identical from here. */}
        {state === 'error' && (
          <ErrorState error={error} title={t('patient.lookup.failed')} onRetry={retry} />
        )}

        {state === 'empty' && (
          <EmptyState
            icon={<SearchX size={48} color={C.textMuted} strokeWidth={1.5} aria-hidden />}
            title={t('patient.lookup.noResults')}
            description={t('patient.lookup.noResultsBody')}
            action={(
              <Button variant="outline" size="sm" className="min-h-11" onClick={clear} data-testid="lookup-clear">
                {t('common.actions.clear')}
              </Button>
            )}
          />
        )}

        {state === 'content' && (
          <>
            <div style={{ fontSize: '0.75rem', color: C.textMuted }} data-testid="lookup-count">
              {t('patient.lookup.count', { count: results.length })}
            </div>

            {results.map(p => (
              <MobilePatientResultCard
                key={p.id}
                patient={p}
                onOpen={(hit) => navigate('patient-detail', { patientId: hit.id, id: hit.id })}
              />
            ))}

            {/* The query is unpaged at the BFF, so the cap is a client-side
                truth — said out loud rather than silently truncating. */}
            {truncated && (
              <div data-testid="lookup-capped" style={{ fontSize: '0.6875rem', color: C.textMuted, textAlign: 'center', padding: '0.5rem 0' }}>
                {t('patient.lookup.capped', { count: formatNumber(RESULT_CAP) })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PatientLookupScreen;
